import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollText, Plus, Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

interface Contract {
  id: string;
  contractNumber: number;
  accountId: string;
  accountName: string;
  businessProfileId: string;
  businessProfileName: string;
  title: string;
  description: string | null;
  startDate: string;
  endDate: string | null;
  autoRenew: boolean;
  renewalPeriodMonths: number | null;
  status: string;
  notes: string | null;
  scheduleCount: number;
  createdAt: string;
}

interface Account {
  id: string;
  name: string;
}

interface BusinessProfile {
  id: string;
  name: string;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  draft: 'secondary',
  active: 'success',
  expired: 'warning',
  terminated: 'destructive',
  cancelled: 'secondary',
};

export function ContractsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contracts', { page, search, status: statusFilter }],
    queryFn: async () => {
      const result = await api.getContracts({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      return result;
    },
  });

  const { data: accountsData } = useQuery({
    queryKey: ['invoiceAccounts'],
    queryFn: () => api.getInvoiceAccounts(),
  });

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const contracts = (data?.data || []) as Contract[];
  const pagination = data?.pagination;
  const accounts = (accountsData?.data || []) as Account[];
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createContract(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateContract(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      setDialogOpen(false);
      setEditingContract(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });

  function openCreate() {
    setEditingContract(null);
    setDialogOpen(true);
  }

  function openEdit(contract: Contract) {
    setEditingContract(contract);
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {
      accountId: formData.get('accountId'),
      businessProfileId: formData.get('businessProfileId'),
      title: formData.get('title'),
      description: formData.get('description') || null,
      startDate: formData.get('startDate'),
      endDate: formData.get('endDate') || null,
      autoRenew: formData.get('autoRenew') === 'on',
      renewalPeriodMonths: formData.get('renewalPeriodMonths') ? parseInt(formData.get('renewalPeriodMonths') as string) : null,
      status: formData.get('status') || 'draft',
      notes: formData.get('notes') || null,
    };

    if (editingContract) {
      updateMutation.mutate({ id: editingContract.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Contracts</h1>
          <p className="text-muted-foreground">Manage customer contracts and billing terms</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Contract
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contracts..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="terminated">Terminated</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : contracts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ScrollText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No contracts found</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">#</th>
                  <th className="text-left p-3 text-sm font-medium">Title</th>
                  <th className="text-left p-3 text-sm font-medium">Account</th>
                  <th className="text-left p-3 text-sm font-medium">Business</th>
                  <th className="text-left p-3 text-sm font-medium">Period</th>
                  <th className="text-left p-3 text-sm font-medium">Status</th>
                  <th className="text-left p-3 text-sm font-medium">Schedules</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-muted/30">
                    <td className="p-3 text-sm text-muted-foreground">{c.contractNumber}</td>
                    <td className="p-3 text-sm font-medium">{c.title}</td>
                    <td className="p-3 text-sm">{c.accountName}</td>
                    <td className="p-3 text-sm">{c.businessProfileName}</td>
                    <td className="p-3 text-sm">
                      {formatDate(c.startDate)} — {c.endDate ? formatDate(c.endDate) : 'Ongoing'}
                    </td>
                    <td className="p-3">
                      <Badge variant={statusVariant[c.status] || 'secondary'}>{c.status}</Badge>
                    </td>
                    <td className="p-3 text-sm">{c.scheduleCount}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => { if (confirm('Delete this contract?')) deleteMutation.mutate(c.id); }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} contract{pagination.total !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingContract ? 'Edit Contract' : 'New Contract'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="accountId">Account *</Label>
                <select
                  name="accountId"
                  id="accountId"
                  required
                  defaultValue={editingContract?.accountId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select account</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="businessProfileId">Business Profile *</Label>
                <select
                  name="businessProfileId"
                  id="businessProfileId"
                  required
                  defaultValue={editingContract?.businessProfileId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select profile</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input name="title" id="title" required defaultValue={editingContract?.title || ''} />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea name="description" id="description" rows={2} defaultValue={editingContract?.description || ''} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate">Start Date *</Label>
                <Input
                  type="date"
                  name="startDate"
                  id="startDate"
                  required
                  defaultValue={editingContract?.startDate ? editingContract.startDate.split('T')[0] : ''}
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  type="date"
                  name="endDate"
                  id="endDate"
                  defaultValue={editingContract?.endDate ? editingContract.endDate.split('T')[0] : ''}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  name="status"
                  id="status"
                  defaultValue={editingContract?.status || 'draft'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="terminated">Terminated</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <Label htmlFor="renewalPeriodMonths">Renewal Period (months)</Label>
                <Input
                  type="number"
                  name="renewalPeriodMonths"
                  id="renewalPeriodMonths"
                  min="1"
                  defaultValue={editingContract?.renewalPeriodMonths || ''}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="autoRenew"
                id="autoRenew"
                defaultChecked={editingContract?.autoRenew || false}
                className="h-4 w-4"
              />
              <Label htmlFor="autoRenew">Auto-renew</Label>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingContract?.notes || ''} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingContract ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
